// ============================================================
// CannaSpy Map — layer definitions
// ============================================================
// All Mapbox layer specs live here. The component composes them;
// it does not author them inline. Keeps paint/layout config in one
// auditable place.

import type { LayerSpecification } from 'mapbox-gl'
import { PALETTE, TRACK_STATE_COLORS } from './mapStyle'
import { ZOOM } from './types'

// Native mapbox-gl layer spec. `source` may be omitted on specs that are
// added through DispensaryMap's `overlays` prop — the overlay id is
// injected as the source at addLayer time.
export type MapLayerSpec = Omit<LayerSpecification, 'source'> & { source?: string }

// ------------------------------------------------------------
// Source IDs — referenced by layers and by data updates.
// ------------------------------------------------------------
export const SOURCE = {
  COMPETITORS: 'cs-competitors',
  DISPENSARIES: 'cs-dispensaries',
  ZIP_HEAT: 'cs-zip-heat',
  DELIVERY_ZONES: 'cs-delivery-zones',
} as const

// ------------------------------------------------------------
// Zip-code choropleth (market-heat at statewide / metro zoom)
// ------------------------------------------------------------
// Drives off `density` (0–1) on each zip feature. Fill gradient:
//   low density  → bg-surface (nearly invisible)
//   high density → coral (alert) with higher opacity
// Only visible at state/region/metro zooms — fades out before
// individual pins come in, so the map never looks busy.
export const zipHeatFillLayer: MapLayerSpec = {
  id: 'cs-zip-heat-fill',
  type: 'fill',
  source: SOURCE.ZIP_HEAT,
  maxzoom: ZOOM.NEIGHBORHOOD,
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'density'], 0],
      0, PALETTE.bgSurface,
      0.25, PALETTE.accentTrust,
      0.5, PALETTE.accentBlock,
      0.75, PALETTE.accentAlert,
      1.0, '#ff4470',
    ],
    'fill-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ZOOM.STATE, 0.55,
      ZOOM.METRO, 0.40,
      ZOOM.NEIGHBORHOOD, 0.0, // fade out as pins appear
    ],
  },
}

export const zipHeatOutlineLayer: MapLayerSpec = {
  id: 'cs-zip-heat-outline',
  type: 'line',
  source: SOURCE.ZIP_HEAT,
  maxzoom: ZOOM.NEIGHBORHOOD,
  paint: {
    'line-color': PALETTE.borderSubtle,
    'line-width': 0.5,
  },
}

// ------------------------------------------------------------
// Heatmap (competitor density as a smooth surface)
// ------------------------------------------------------------
// Good secondary view — complements the zip choropleth with a
// smoother, non-polygon density signal. Toggle via a UI control.
export const competitorHeatmapLayer: MapLayerSpec = {
  id: 'cs-competitor-heatmap',
  type: 'heatmap',
  source: SOURCE.COMPETITORS,
  maxzoom: ZOOM.METRO + 1,
  paint: {
    'heatmap-weight': [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'heat_score'], 0.5],
      0, 0.2,
      1, 1.0,
    ],
    'heatmap-intensity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ZOOM.STATE, 1,
      ZOOM.METRO, 3,
    ],
    'heatmap-color': [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0, 'rgba(13,15,17,0)',
      0.2, 'rgba(59,139,212,0.35)', // trust-blue
      0.5, 'rgba(186,117,23,0.55)', // amber
      0.8, 'rgba(212,83,126,0.70)', // coral
      1.0, 'rgba(255,68,112,0.85)',
    ],
    'heatmap-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ZOOM.STATE, 10,
      ZOOM.METRO, 28,
    ],
    'heatmap-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ZOOM.METRO, 0.85,
      ZOOM.METRO + 1, 0,
    ],
  },
}

// ------------------------------------------------------------
// Cluster bubbles (competitor counts, mid-zoom)
// ------------------------------------------------------------
export const clusterCircleLayer: MapLayerSpec = {
  id: 'cs-clusters',
  type: 'circle',
  source: SOURCE.COMPETITORS,
  filter: ['has', 'point_count'],
  minzoom: ZOOM.REGION,
  paint: {
    'circle-color': [
      'step',
      ['get', 'point_count'],
      PALETTE.accentTrust, 10,
      PALETTE.accentBlock, 50,
      PALETTE.accentAlert,
    ],
    'circle-radius': [
      'step',
      ['get', 'point_count'],
      14, 10,
      20, 50,
      28,
    ],
    'circle-stroke-color': PALETTE.bgBase,
    'circle-stroke-width': 2,
    'circle-opacity': 0.92,
  },
}

export const clusterCountLayer: MapLayerSpec = {
  id: 'cs-cluster-count',
  type: 'symbol',
  source: SOURCE.COMPETITORS,
  filter: ['has', 'point_count'],
  minzoom: ZOOM.REGION,
  layout: {
    'text-field': ['get', 'point_count_abbreviated'],
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 11,
  },
  paint: {
    'text-color': PALETTE.bgBase,
  },
}

// ------------------------------------------------------------
// Individual competitor markers
// ------------------------------------------------------------
// Color by track_state: teal=tracking, amber=blocked,
// trust-blue=delivery operator, textSecondary=untracked.
// track_state values match the map API: 'tracking' | 'blocked' | 'both' | 'untracked'.
// Slightly smaller ring for delivery so retail storefronts read first.
export const competitorPointLayer: MapLayerSpec = {
  id: 'cs-competitor-point',
  type: 'circle',
  source: SOURCE.COMPETITORS,
  filter: ['!', ['has', 'point_count']],
  minzoom: ZOOM.METRO,
  paint: {
    'circle-color': [
      'match',
      ['coalesce', ['get', 'track_state'], 'untracked'],
      'blocked', PALETTE.accentBlock,
      'both', PALETTE.accentBlock,
      'tracking', PALETTE.accentIntel,
      /* default */ [
        'match',
        ['coalesce', ['get', 'business_type'], 'storefront'],
        'delivery', PALETTE.accentTrust,
        'both', PALETTE.accentTrust,
        /* default */ PALETTE.textSecondary,
      ],
    ],
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ZOOM.METRO, 4,
      ZOOM.DETAIL, 7,
    ],
    'circle-stroke-color': PALETTE.bgBase,
    'circle-stroke-width': 1.5,
    'circle-opacity': 0.95,
  },
}

// ------------------------------------------------------------
// Live dispensary pins — four-layer visual system
// ------------------------------------------------------------
// Driven by track_state + business_type + blocked_by_competitor.
//
// Layer stack (bottom → top):
//   1. cs-dispensary-incoming-ring — amber stroke ring (only when blocked_by_competitor)
//   2. cs-dispensary-ring          — ambient glow by business_type
//   3. cs-dispensary-point         — outer circle by business_type, dim at 55% for prospects
//   4. cs-dispensary-inner-dot     — inner dot by track_state (orange/cyan/yellow)
//
// Outer circle colors: storefront → teal #1d9e75, delivery/both → blue #3b8bd4
// Inner dot colors:   tracking → orange #fb923c, blocked → cyan #67e8f9, both → yellow #fde047
// Amber #ba7517 is ONLY for incoming blocks — never for own-blocked pins.

// Amber stroke ring — signals a competitor has been blocked BY ANOTHER ORG.
// Only visible when blocked_by_competitor === true.
export const dispensaryIncomingRingLayer: MapLayerSpec = {
  id: 'cs-dispensary-incoming-ring',
  type: 'circle',
  source: SOURCE.DISPENSARIES,
  filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'blocked_by_competitor'], true]],
  paint: {
    'circle-color': 'rgba(0,0,0,0)',
    'circle-radius': [
      '+', 20,
      ['case', ['boolean', ['feature-state', 'hover'], false], 4, 0],
    ] as unknown as number,
    'circle-opacity': 0,
    'circle-stroke-color': PALETTE.accentBlock,
    'circle-stroke-width': 2,
    'circle-stroke-opacity': 0.85,
  },
}

// Ambient glow ring — colored by business_type, rendered below the fill.
// An org's OWN store (server `is_own`) glows purple instead — it is the
// operator's own location, not a trackable competitor. The purple ties to the
// OwnLocationMarker overlay + the "This is your location" popup (accentRoi).
export const dispensaryRingLayer: MapLayerSpec = {
  id: 'cs-dispensary-ring',
  type: 'circle',
  source: SOURCE.DISPENSARIES,
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': [
      'case',
      ['==', ['get', 'is_own'], true],              PALETTE.accentRoi,
      ['==', ['get', 'business_type'], 'delivery'], PALETTE.accentTrust,
      ['==', ['get', 'business_type'], 'both'],     PALETTE.accentTrust,
      PALETTE.accentIntel,
    ] as unknown as string,
    'circle-radius': [
      '+', 24,
      ['case', ['boolean', ['feature-state', 'hover'], false], 4, 0],
    ] as unknown as number,
    'circle-opacity': 0.20,
    'circle-stroke-width': 0,
  },
}

// Inner dot — color by org relationship (tracking/blocked/both). Hidden for prospects.
export const dispensaryInnerDotLayer: MapLayerSpec = {
  id: 'cs-dispensary-inner-dot',
  type: 'circle',
  source: SOURCE.DISPENSARIES,
  filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'track_state'], 'untracked']],
  paint: {
    'circle-color': [
      'match',
      ['get', 'track_state'],
      'both',     TRACK_STATE_COLORS.both,
      'blocked',  TRACK_STATE_COLORS.blocked,
      'tracking', TRACK_STATE_COLORS.tracking,
      'rgba(0,0,0,0)',
    ] as unknown as string,
    'circle-radius': 5,
    'circle-stroke-width': 0,
    'circle-opacity': 1,
  },
}

export const dispensaryClusterLayer: MapLayerSpec = {
  id: 'cs-dispensary-cluster',
  type: 'circle',
  source: SOURCE.DISPENSARIES,
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': PALETTE.accentAlert,
    'circle-radius': [
      'step',
      ['get', 'point_count'],
      24, 10,
      30, 50,
      36,
    ] as unknown as number,
    'circle-stroke-color': PALETTE.bgBase,
    'circle-stroke-width': 1.5,
    'circle-opacity': 1,
  },
}

export const dispensaryClusterCountLayer: MapLayerSpec = {
  id: 'cs-dispensary-cluster-count',
  type: 'symbol',
  source: SOURCE.DISPENSARIES,
  filter: ['has', 'point_count'],
  layout: {
    'text-field': ['get', 'point_count_abbreviated'],
    // Space Mono is not a Mapbox glyph font — use closest available
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 12,
  },
  paint: {
    'text-color': '#ffffff',
  },
}

// Outer circle — colored by business_type only. Prospects dim at 55%.
// An org's OWN store (server `is_own`) is purple + full opacity — it is a
// non-actionable own location (never a green trackable competitor pin),
// consistent with the OwnLocationMarker overlay that also sits on the
// storefront. This catches the store's DELIVERY listing, which has no
// overlapping marker (geocoded to a different point).
export const dispensaryPointLayer: MapLayerSpec = {
  id: 'cs-dispensary-point',
  type: 'circle',
  source: SOURCE.DISPENSARIES,
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': [
      'case',
      ['==', ['get', 'is_own'], true],              PALETTE.accentRoi,
      ['==', ['get', 'business_type'], 'delivery'], PALETTE.accentTrust,
      ['==', ['get', 'business_type'], 'both'],     PALETTE.accentTrust,
      PALETTE.accentIntel,
    ] as unknown as string,
    'circle-radius': [
      '+', 13,
      ['case', ['boolean', ['feature-state', 'hover'], false], 4, 0],
    ] as unknown as number,
    'circle-stroke-color': PALETTE.bgBase,
    'circle-stroke-width': 1.5,
    // own → 100%, tracked/blocked → 100%, prospect → 55%
    'circle-opacity': [
      'case',
      ['boolean', ['feature-state', 'hover'], false], 1,
      ['==', ['get', 'is_own'], true],                1,
      ['!=', ['get', 'track_state'], 'untracked'],    1,
      0.55,
    ] as unknown as number,
  },
}

// dispensaryPointLayer variant that dims unenriched pins to near-invisible (0.06 opacity)
// while fully showing enriched pins. Used for enriched-only map filter modes.
export const dispensaryEnrichedOnlyPointLayer: MapLayerSpec = {
  ...dispensaryPointLayer,
  id: 'cs-dispensary-point',
  paint: {
    ...dispensaryPointLayer.paint,
    'circle-opacity': [
      'case',
      ['boolean', ['feature-state', 'hover'], false], 1,
      ['==', ['get', 'enriched'], true], 0.9,
      0.06,
    ] as unknown as number,
  },
}

// ------------------------------------------------------------
// Delivery service coverage polygons (opt-in overlay)
// ------------------------------------------------------------
// Rendered only when a specific delivery operator is selected from
// the sidebar. Keeps the map readable — we never draw all delivery
// zones at once (hundreds of overlapping polygons = visual chaos).
export const deliveryCoverageFillLayer: MapLayerSpec = {
  id: 'cs-delivery-coverage-fill',
  type: 'fill',
  source: SOURCE.DELIVERY_ZONES,
  paint: {
    'fill-color': PALETTE.accentTrust,
    'fill-opacity': 0.18,
  },
}

export const deliveryCoverageOutlineLayer: MapLayerSpec = {
  id: 'cs-delivery-coverage-outline',
  type: 'line',
  source: SOURCE.DELIVERY_ZONES,
  paint: {
    'line-color': PALETTE.accentTrust,
    'line-width': 1.5,
    'line-dasharray': [2, 2],
  },
}

// ------------------------------------------------------------
// Dispensary layer stack (bottom → top) — the 6 live-pin layers
// ------------------------------------------------------------
// DispensaryMap adds these imperatively on every style.load. Specs are
// reused verbatim; only `minzoom` is injected at compose time.
export function dispensaryLayerStack(enrichedOnly: boolean, pinMinZoom: number): MapLayerSpec[] {
  return [
    dispensaryIncomingRingLayer,
    dispensaryRingLayer,
    enrichedOnly ? dispensaryEnrichedOnlyPointLayer : dispensaryPointLayer,
    dispensaryInnerDotLayer,
    dispensaryClusterLayer,
    dispensaryClusterCountLayer,
  ].map(spec => ({ ...spec, minzoom: pinMinZoom }))
}

// ------------------------------------------------------------
// Layer render order (bottom → top)
// ------------------------------------------------------------
// Export the canonical stack so the component can map over it.
export const LAYER_STACK: MapLayerSpec[] = [
  zipHeatFillLayer,
  zipHeatOutlineLayer,
  deliveryCoverageFillLayer,
  deliveryCoverageOutlineLayer,
  competitorHeatmapLayer,
  clusterCircleLayer,
  clusterCountLayer,
  competitorPointLayer,
]
